/**
 * Section Editor Dialog Component
 *
 * Dialog for editing a single section's props.
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { PageSection } from '../types';
import { getSectionType } from './sectionRegistry';
import {
  PAGE_BUILDER_SCHEMA,
  getBuilderSectionTypeOptions,
  getDefaultPropsForSectionType,
  type BuilderSectionTypeOption,
} from '@zaviago/storefront-config';

// Schema-derived section types (module-scope cache)
const BUILDER_SECTION_TYPES: BuilderSectionTypeOption[] = getBuilderSectionTypeOptions(PAGE_BUILDER_SCHEMA);

interface SectionEditorProps {
  section: PageSection | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (section: PageSection) => void;
}

export function SectionEditor({ section, open, onOpenChange, onSave }: SectionEditorProps) {
  const [sectionType, setSectionType] = useState<string>('');
  const [props, setProps] = useState<Record<string, unknown>>({});

  // Initialize form when section changes
  useEffect(() => {
    if (section) {
      setSectionType(section.type);
      setProps(section.props || {});
    } else {
      setSectionType('');
      setProps({});
    }
  }, [section]);

  const sectionDef = sectionType ? getSectionType(sectionType) : null;

  const handleSave = () => {
    if (!sectionType) return;

    // Use schema defaults (fallback to registry for compatibility)
    const schemaDefaults = getDefaultPropsForSectionType(PAGE_BUILDER_SCHEMA, sectionType);
    const registryDef = getSectionType(sectionType);
    const registryDefaults = registryDef?.defaultProps || {};

    // Merge: schema defaults take precedence, then registry, then user props
    const finalProps = { ...registryDefaults, ...schemaDefaults, ...props };

    onSave({
      type: sectionType,
      props: finalProps,
    });

    onOpenChange(false);
  };

  const handlePropChange = (key: string, value: unknown) => {
    setProps(prev => ({ ...prev, [key]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {section ? 'Edit Section' : 'Add Section'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Section Type Selector */}
          <div className="space-y-2">
            <Label>Section Type</Label>
            <Select value={sectionType} onValueChange={setSectionType}>
              <SelectTrigger>
                <SelectValue placeholder="Select section type" />
              </SelectTrigger>
              <SelectContent>
                {BUILDER_SECTION_TYPES.map((type) => (
                  <SelectItem key={type.type} value={type.type}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(() => {
              const schemaDef = BUILDER_SECTION_TYPES.find((t) => t.type === sectionType);
              return schemaDef ? (
                <p className="text-xs text-muted-foreground">{schemaDef.description}</p>
              ) : null;
            })()}
          </div>

          {/* Dynamic Props Editor */}
          {sectionDef && (
            <div className="space-y-4 border-t pt-4">
              {/* Product Grid Props */}
              {sectionType === 'product-grid' && (
                <div className="space-y-2">
                  <Label>Limit</Label>
                  <Input
                    type="number"
                    value={(props.limit as number) || 8}
                    onChange={(e) => handlePropChange('limit', parseInt(e.target.value, 10) || 8)}
                    min={1}
                    max={50}
                  />
                </div>
              )}

              {/* Category Grid Props */}
              {sectionType === 'category-grid' && (
                <div className="space-y-2">
                  <Label>Parent Handle (optional)</Label>
                  <Input
                    value={(props.parentHandle as string) || ''}
                    onChange={(e) => handlePropChange('parentHandle', e.target.value)}
                    placeholder="Leave empty for root categories"
                  />
                </div>
              )}

              {/* Announcement Bar Props */}
              {sectionType === 'announcement-bar' && (
                <>
                  <div className="space-y-2">
                    <Label>Message</Label>
                    <Input
                      value={(props.message as string) || ''}
                      onChange={(e) => handlePropChange('message', e.target.value)}
                      placeholder="Welcome to our store!"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Link Text (optional)</Label>
                    <Input
                      value={(props.linkText as string) || ''}
                      onChange={(e) => handlePropChange('linkText', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Link URL (optional)</Label>
                    <Input
                      value={(props.linkHref as string) || ''}
                      onChange={(e) => handlePropChange('linkHref', e.target.value)}
                      placeholder="/products"
                    />
                  </div>
                </>
              )}

              {/* Text Block Props */}
              {sectionType === 'text-block' && (
                <>
                  <div className="space-y-2">
                    <Label>Heading (optional)</Label>
                    <Input
                      value={(props.heading as string) || ''}
                      onChange={(e) => handlePropChange('heading', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Body</Label>
                    <Textarea
                      value={(props.body as string) || ''}
                      onChange={(e) => handlePropChange('body', e.target.value)}
                      rows={6}
                      placeholder="Enter markdown content..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Alignment</Label>
                    <Select
                      value={(props.alignment as string) || 'left'}
                      onValueChange={(value) => handlePropChange('alignment', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="left">Left</SelectItem>
                        <SelectItem value="center">Center</SelectItem>
                        <SelectItem value="right">Right</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!sectionType}>
            {section ? 'Save Changes' : 'Add Section'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
